import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../api';

export default function NotificationBell({ user, onReviewClick }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const load = useCallback(async () => {
    try {
      const data = await fetchNotifications();
      setNotifications(data.notifications || []);
    } catch {
      // silently fail
    }
  }, []);

  // Poll every 30s
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click or touch
  useEffect(() => {
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const handleOpen = () => {
    const isOpening = !open;
    setOpen(v => !v);
    if (isOpening && unreadCount > 0) {
      // Mark all as read after 1.5s — gives user a moment to see which are new
      setTimeout(async () => {
        try {
          await markAllNotificationsRead();
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch {}
      }, 1500);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleMarkRead = async (notification) => {
    try {
      if (!notification.read) {
        await markNotificationRead(notification.id);
        setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
      }
      if (onReviewClick && notification.reviewId) {
        onReviewClick(notification.reviewId);
        setOpen(false);
      }
    } catch {}
  };

  function timeAgo(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        style={{
          position: 'relative', width: 38, height: 38, borderRadius: 10,
          background: open ? 'var(--surface2)' : 'transparent',
          border: '1px solid var(--border)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? 'var(--blue)' : 'var(--text2)', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.color = 'var(--blue)'; }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text2)'; } }}
        title="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#ef4444', color: '#fff',
            borderRadius: '50%', width: 18, height: 18,
            fontSize: 10, fontWeight: 800, fontFamily: 'var(--mono)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff', lineHeight: 1,
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="notif-dropdown" style={{
          position: 'absolute', right: 0, top: 44,
          width: 320, maxHeight: 420, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          zIndex: 1050, animation: 'fadeUp 0.15s ease-out',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              Notifications {unreadCount > 0 && <span style={{ color: 'var(--blue)' }}>({unreadCount})</span>}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, color: 'var(--blue)', padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          {notifications.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No notifications yet
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.id}
                className="notif-item"
                onClick={() => handleMarkRead(n)}
                onTouchEnd={e => { e.preventDefault(); handleMarkRead(n); }}
                style={{
                  padding: '12px 16px', minHeight: 56,
                  borderBottom: '1px solid var(--border)',
                  background: n.read ? 'var(--surface)' : 'rgba(14,165,233,0.06)',
                  cursor: 'pointer', transition: 'background 0.15s',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = n.read ? 'var(--surface)' : 'rgba(14,165,233,0.06)'}
              >
                {/* Icon */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(14,165,233,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}>
                  {n.type === 'comment' ? '💬' : n.type === 'reply' ? '↩️' : n.type === 'reaction' ? (n.emoji || '😊') : n.type === 'new_kahani_company' ? '🏢' : n.type === 'new_kahani_role' ? '💼' : '👍'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.5 }}>
                    {(() => {
                      const actor = n.actorHkId || (/^User \d{4}$/.test(n.actorName) ? null : n.actorName) || 'Someone';
                      return n.type === 'comment' ? (
                        <><strong>{actor}</strong> commented on your kahani about <strong>{n.companyName}</strong></>
                      ) : n.type === 'reply' ? (
                        <><strong>{actor}</strong> replied to your comment on <strong>{n.companyName}</strong></>
                      ) : n.type === 'reaction' ? (
                        <><strong>{actor}</strong> reacted {n.emoji} to your comment on <strong>{n.companyName}</strong></>
                      ) : n.type === 'new_kahani_company' ? (
                        <><strong>{actor}</strong> posted a new kahani at <strong>{n.companyName}</strong></>
                      ) : n.type === 'new_kahani_role' ? (
                        <><strong>{actor}</strong> posted a new kahani for <strong>{n.role}</strong> at <strong>{n.companyName}</strong></>
                      ) : (
                        <><strong>{actor}</strong> liked your kahani about <strong>{n.companyName}</strong></>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                    {timeAgo(n.createdAt)}
                  </div>
                </div>
                {!n.read && (
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--blue)', flexShrink: 0, marginTop: 5 }} />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
